'use client'

import { useState, useEffect } from 'react'
import Head from 'next/head'
import { useSession } from 'next-auth/react'
import { useTheme } from 'next-themes'
import { Toaster } from 'react-hot-toast'
import { useDeliveryAppContext } from '@/context/deliveryApp/DeliveryAppContext'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import useRefreshLocalStorage from '@/hooks/useRefreshLocalStorage'
import Header from '@/app/components/Layout/Header'
import Footer from '@/app/components/Layout/Footer'
import { DeliverOrderHeaderBar } from '@/components/LayoutApp/HeaderBar'
import { Transition } from '@headlessui/react'

const MainLayoutProvider = ({ children }) => {
  const { currentScreen, showHeaderBar, showVariantHeaderBar, showFooterBar } =
    useDeliveryAppContext()
  const { resolvedTheme } = useTheme()
  useRefreshLocalStorage()

  const { status } = useSession()

  const isAuth = status === 'authenticated'

  const isOnline = useOnlineStatus()
  const [showNotification, setShowNotification] = useState(false)

  useEffect(() => {
    if (!isOnline) {
      setShowNotification(true)
    } else {
      if (showNotification) {
        setTimeout(() => {
          setShowNotification(false)
        }, 2000)
      }
    }
  }, [isOnline])

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, height=device-height, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=no"
        />
        {resolvedTheme === 'dark' ? (
          <meta name="theme-color" content="#101216" />
        ) : (
          <meta name="theme-color" content="#ffffff" />
        )}
      </Head>
      <div className="min-h-screen max-h-full bg0 w-full max-w-3xl flex flex-col justify-between items-center text-payne-primary dark:text-[#e0e0e0] bg-white dark:bg-black-darkMode">
        {showHeaderBar && <Header />}
        {showVariantHeaderBar && <DeliverOrderHeaderBar />}
        <Transition
          show={showNotification}
          enter="transition ease-in-out duration-150 transform"
          enterFrom="-translate-y-full"
          enterTo="translate-y-0"
          leave="transition ease-in-out duration-300 transform"
          leaveFrom="translate-y-0"
          leaveTo="-translate-y-full"
          className={`top-[39px] relative flex items-center justify-center w-full h-14 text-center`}
        >
          <p
            className={`text-xs font-medium text-white relative top-[14px] border backdrop-blur-sm rounded-full px-2 py-1 ${
              isOnline
                ? 'bg-green-500/75 border border-green-500'
                : 'bg-red-500/75 border-red-500'
            }`}
          >
            {isOnline
              ? 'Estas de vuelta. Disfruta de la app'
              : 'Estas desconectado. Revisa tu conexión'}
          </p>
        </Transition>
        <main
          className={`w-full max-h-full flex-1 px-3 flex flex-col ${
            (showHeaderBar === false && showVariantHeaderBar === false) ||
            (showFooterBar === false && currentScreen !== 'deliverOrder')
              ? 'justify-center'
              : 'justify-start'
          } items-center ${
            showHeaderBar || showVariantHeaderBar ? 'mt-[56px] pt-3 ' : 'pt-7'
          } ${showFooterBar ? 'pb-[68px]' : 'pb-7'}`}
        >
          {children}
        </main>
        {showFooterBar && isAuth && <Footer />}
      </div>
      <Toaster
        position="bottom-center"
        reverseOrder={false}
        containerStyle={{
          bottom: '70px'
        }}
        toastOptions={{
          className:
            'dark:!bg-black-darkMode/50 !bg-white/50 dark:text-white-darkMode border border-gray-300 dark:border-gray-darkMode backdrop-blur-md !rounded-full',
          success: {
            style: {
              border: '1px solid #61d345'
            }
          },
          error: {
            style: {
              border: '1px solid #f15249'
            }
          }
        }}
      />
    </>
  )
}

export default MainLayoutProvider
